import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DomainListItem } from '@app/core/interfaces/c5/domain-list-item';
import { EndUserRepository } from '@app/core/repositories/end-user.repository';
import { EnterpriseRepository } from '@app/core/repositories/enterprise.repository';
import { RouterService } from '@app/routing/router.service';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { sortBy } from '@app/utils/sort/sort-by';
import { Authorization } from '@business/end-user/models/authorization';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-quick-jump-dialog',
  templateUrl: './quick-jump-dialog.component.html',
  styles: [],
  standalone: false,
})
export class QuickJumpDialogComponent extends DialogComponent<void, void> implements OnInit {
  @ViewChild('userSelect', { read: NgSelectComponent })
  userSelect: NgSelectComponent;

  enterprises: DomainListItem[] = [];
  form = this.buildForm();
  routes: string[] = [];
  users: EndUserLite[] = [];

  private allRoutes = ['end-user-personalization', 'end-user-features', 'end-user-devices'];

  constructor(
    private enterpriseRepository: EnterpriseRepository,
    private routerService: RouterService,
    private userRepository: EndUserRepository,
    ngbActiveModal: NgbActiveModal
  ) {
    super(ngbActiveModal);
  }

  ngOnInit(): void {
    this.routes = this.allRoutes;

    this.form.patchValue({
      enterpriseId: this.routerService.currentRouteData.params.enterpriseId,
      userId: this.routerService.currentRouteData.params.userId,
    });

    this.enterpriseRepository.listEnterprises(false).subscribe((enterprises) => {
      this.enterprises = enterprises.sort(sortBy('name'));
    });

    this.form.controls.enterpriseId.valueChanges.pipe(untilDestroyed(this)).subscribe((enterpriseId) => {
      this.loadUsers(enterpriseId);
    });

    this.form.controls.userId.valueChanges.pipe(untilDestroyed(this)).subscribe((userId) => {
      this.updateRoutes(userId);
    });

    if (this.form.value.enterpriseId != null) {
      this.loadUsers(this.form.value.enterpriseId, this.form.value.userId);
    }
  }

  navigate(): void {
    this.routerService.navigateToRoute(this.form.value.route, {
      enterpriseId: this.form.value.enterpriseId,
      userId: this.form.value.userId,
    });

    this.close();
  }

  private loadUsers(enterpriseId: string, userId?: string): void {
    this.form.controls.userId.setValue(null);
    this.users = [];

    this.userRepository.list(enterpriseId).subscribe((users) => {
      this.form.controls.userId.setValue(userId ?? null);
      this.users = users.sort(sortBy('loginName'));

      if (userId != null) {
        this.updateRoutes(userId);
      }
    });
  }

  private updateRoutes(userId: string): void {
    const user = this.users.find((user) => user.loginName === userId);

    if (user?.auth === Authorization.FULL) {
      this.routes = this.allRoutes;
    } else {
      this.routes = this.allRoutes.filter((route) => route !== 'end-user-devices');
    }
  }

  private buildForm() {
    return new FormGroup({
      enterpriseId: new FormControl<string | null>(null),
      route: new FormControl('end-user-personalization', { nonNullable: true }),
      userId: new FormControl<string | null>(null),
    });
  }
}
